import React, { useEffect, useRef, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import cx from "classnames";
import { useSpring, animated } from "@react-spring/web";

import SwitchButton from "src/components/switchButton";
import {
  GatingCatalogRecord,
  GatingConfigurationResponse,
  GatingConversionType,
} from "src/types/experiences/storyboardSettings";

import { DropDown } from "../dropdown";
import { buildCleverstoryUrl } from "src/pages/experiences/utils/cleverstoryRedirect";
import { createAutoGatingSeed } from "./constants";
import styles from "./gatingTriggersCard.module.scss";

interface AutoGatingRowProps {
  config?: GatingConfigurationResponse;
  editable: boolean;
  progressiveCatalogOptions: GatingCatalogRecord[];
  // eslint-disable-next-line no-unused-vars
  onEdit: (data: GatingConfigurationResponse, silent?: boolean) => void;
  catalogLoading?: boolean;
  catalogFetched?: boolean;
  catalogHasMore?: boolean;
  loadMoreCatalog?: () => void;
}

const CONVERSION_TYPES: {
  value: GatingConversionType;
  label: string;
  description: string;
}[] = [
  {
    value: "RICHLEADS",
    label: "Rich Leads",
    description:
      "Capture high-intent prospects by asking deeper qualification questions after strong engagement.",
  },
  {
    value: "MORELEADS",
    label: "More Leads",
    description:
      "Capture a larger number of prospects with shorter forms triggered earlier in the journey.",
  },
];

const AutoGatingRow: React.FC<AutoGatingRowProps> = ({
  config,
  editable,
  progressiveCatalogOptions,
  onEdit,
  catalogLoading,
  catalogFetched,
  catalogHasMore,
  loadMoreCatalog,
}) => {
  const { workspaceId } = useParams();
  const enabled = Boolean(config?.enabled);
  const contentRef = useRef<HTMLDivElement>(null);

  const [expanded, setExpanded] = useState(enabled);

  const [animation, api] = useSpring(() => ({
    height: 0,
    opacity: 0,
    config: { tension: 280, friction: 28 },
  }));

  useEffect(() => {
    const node = contentRef.current;
    if (!node) return;

    if (!enabled) setExpanded(false);

    api.start({
      height: enabled ? node.scrollHeight : 0,
      opacity: enabled ? 1 : 0,
      onRest: () => {
        if (enabled) setExpanded(true);
      },
    });

    if (!enabled) return;

    const observer = new ResizeObserver(() => {
      api.start({ height: node.scrollHeight });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, api]);

  const conversionType =
    (config?.conversionType as GatingConversionType) ?? null;

  const [dropdownAnimation, dropdownApi] = useSpring(() => ({
    opacity: 0,
    y: -8,
    config: { tension: 280, friction: 28 },
  }));

  useEffect(() => {
    dropdownApi.start({
      opacity: conversionType ? 1 : 0,
      y: conversionType ? 0 : -8,
    });
  }, [conversionType, dropdownApi]);

  const selectedProgressiveId = config?.gating?.id ?? "";

  useEffect(() => {
    if (!catalogFetched || catalogLoading || catalogHasMore) return;
    if (!config?.gating) return;
    const screenValid =
      Boolean(config.gating.progressive) && progressiveCatalogOptions.length > 0;
    if (screenValid) return;

    const screenPresent = progressiveCatalogOptions.some(
      (opt) => opt.id === selectedProgressiveId,
    );
    if (screenPresent) return;
    onEdit(
      {
        ...config,
        smart: false,
        autoGating: true,
        trigger: null,
        optional: false,
        enabled: false,
        gating: null,
        conversionType: null,
      },
      true,
    );
  }, [
    catalogFetched,
    catalogLoading,
    catalogHasMore,
    selectedProgressiveId,
    config?.gating?.progressive,
    progressiveCatalogOptions.length,
  ]);

  useEffect(() => {
    if (!config || selectedProgressiveId) return;
    if (!config.enabled) return;
    onEdit({ ...config, enabled: false }, true);
  }, [selectedProgressiveId]);

  const progressiveOptions = useMemo(
    () =>
      progressiveCatalogOptions.map((g) => ({
        key: g.id,
        label: g.name,
        progressive: g.progressive,
      })),
    [progressiveCatalogOptions],
  );

  const selectedProgressiveOption = useMemo(
    () =>
      progressiveOptions.find((opt) => opt.key === selectedProgressiveId) ?? {
        key: "",
        label: "",
      },
    [progressiveOptions, selectedProgressiveId],
  );

  const handleToggle = () => {
    if (!editable) return;
    const base = config ?? createAutoGatingSeed();
    if (!enabled) {
      onEdit({
        ...base,
        trigger: null,
        smart: false,
        autoGating: true,
        optional: true,
        enabled: true,
      });
      return;
    }
    onEdit({
      ...base,
      smart: false,
      autoGating: true,
      trigger: null,
      optional: false,
      enabled: false,
      gating: null,
      conversionType: null,
    });
  };

  const handleSelectConversionType = (value: GatingConversionType) => {
    if (!editable) return;
    if (!config) return;
    if (config.conversionType === value) return;
    onEdit({ ...config, conversionType: value });
  };

  const handleEditGatingScreen = () => {
    if (!workspaceId || !selectedProgressiveId) return;
    const url = buildCleverstoryUrl(
      `/app/${workspaceId}/gating/${selectedProgressiveId}`,
    );
    if (!url) return;
    const win = window.open(url, "_blank");
    win?.focus();
  };

  const handleSelectProgressive = (key: string) => {
    if (!editable) return;
    if (!config) return;
    const id = key || undefined;
    const selected = id
      ? progressiveCatalogOptions.find((opt) => opt.id === id)
      : undefined;
    onEdit({
      ...config,
      gating: id
        ? { id, name: selected?.name, progressive: selected?.progressive }
        : null,
    });
  };

  return (
    <div className={styles.autoRow}>
      <div className={styles.autoRowContent}>
        <div className={styles.autoRowHeader}>
          <div className={styles.autoRowText}>
            <div className={styles.autoRowTitle}>
              <span className={styles.autoRowTitleText}>Auto Gating</span>
              <span
                className={cx(styles.statusBadge, {
                  [styles.statusBadgeActive]: enabled,
                })}
              >
                {enabled ? "active" : "inactive"}
              </span>
            </div>
            <div className={styles.autoRowDescription}>
              Select the lead capture strategy that fits your campaign goal
            </div>
          </div>
          <div>
            <SwitchButton
              id="autoGatingToggle"
              checked={enabled}
              disabled={!editable}
              handleCheck={handleToggle}
            />
          </div>
        </div>

        <animated.div
          className={cx(styles.expandableWrapper, {
            [styles.expandableWrapperVisible]: expanded,
          })}
          style={animation}
        >
          <div ref={contentRef} className={styles.expandableInner}>
            <div className={styles.conversionTypes}>
              {CONVERSION_TYPES.map((option) => {
                const selected = conversionType === option.value;
                const disabled = !editable || !config;

                return (
                  <div
                    key={option.value}
                    className={cx(styles.conversionContent, {
                      [styles.conversionContentSelected]: selected,
                      [styles.conversionContentDisabled]: disabled,
                    })}
                    onClick={() => {
                      if (disabled) return;
                      handleSelectConversionType(option.value);
                    }}
                  >
                    <label className={styles.conversionRadioWrapper}>
                      <i
                        className={cx(styles.conversionRadio, {
                          "paperflite-radio-button-selected": selected,
                          "paperflite-radio-button-unselected": !selected,
                          [styles.conversionRadioSelected]: selected,
                        })}
                        aria-hidden="true"
                      />
                      <span
                        className={cx(styles.conversionLabel, {
                          [styles.conversionLabelSelected]: selected,
                        })}
                      >
                        {option.label}
                      </span>
                    </label>
                    <div className={styles.conversionDescription}>
                      {option.description}
                    </div>
                  </div>
                );
              })}
            </div>
            {conversionType && (
              <animated.div style={dropdownAnimation}>
                <div className={styles.progressiveSelectWrapper}>
                  <div className={styles.progressiveSelect}>
                    <DropDown
                      selected={selectedProgressiveOption}
                      options={progressiveOptions}
                      placeholder="Select gating screen"
                      loadingLabel="Loading..."
                      loading={catalogLoading}
                      hasMore={catalogHasMore}
                      loadMore={loadMoreCatalog}
                      disabled={!editable}
                      onChange={(key) => handleSelectProgressive(String(key))}
                    />
                  </div>
                  {selectedProgressiveId && (
                    <button
                      type="button"
                      className={styles.editGatingBtn}
                      onClick={handleEditGatingScreen}
                      disabled={!editable}
                      aria-label="Open gating screen"
                    >
                      <i className="paperflite-redirect" />
                    </button>
                  )}
                </div>
              </animated.div>
            )}
          </div>
        </animated.div>
      </div>
    </div>
  );
};

export default AutoGatingRow;
